import * as React from "react";

import { PLAY_BASE_URL } from "@/config";
import { track } from "@/lib/analytics";
import {
  buildPreviewMessages,
  isAwaiting,
  NO_PIN_VERDICTS,
  PREVIEW_READY_TYPE,
  PREVIEW_V2_ACK_TYPE,
  reducePinVerdicts,
  type PinVerdictEvent,
  type ThemePreview,
  type ThemeRecipePayload,
} from "@/features/preview/preview-channel";
import type { PinVerdictReport } from "@/data/pin-verdicts";

export type { ThemePreview, ThemeRecipePayload };

/** Logical viewport the embedded app renders at — a real iPhone-class mobile
 *  width so the web app picks its mobile layout, then scaled down to fit the
 *  portal's preview column. */
const DEVICE = { width: 390, height: 844 };

/** Origin of the embedded app — the `targetOrigin` we post live-preview
 *  messages to (never `*`), and the origin we accept the readiness ping from. */
const PLAY_ORIGIN = new URL(PLAY_BASE_URL).origin;

// The postMessage contract + channel decision live in ./preview-channel (pure,
// unit-tested). This frame owns only the DOM plumbing (iframe, ready/ack, debounce).

/** Build the live-app URL for a brand. The app keys off `brand_id` and renders
 *  the brand's live (published) theme. `cacheBust` changes on publish so the
 *  iframe reloads to pick up freshly-published changes (the app does not poll).
 *  Live draft colours are pushed via postMessage instead of the URL, so editing
 *  never changes `src` (which would reload the iframe on every keystroke). */
function previewUrl(brandId: string, cacheBust: number): string {
  const u = new URL(PLAY_BASE_URL);
  u.searchParams.set("brand_id", brandId);
  // Cache-buster: a changing query forces a real (uncached) navigation on
  // reload, on top of the element remount keyed off `reloadKey`.
  if (cacheBust) u.searchParams.set("_r", String(cacheBust));
  return u.toString();
}

/**
 * A live preview of the real pop.in web app for a brand, embedded in an iframe
 * at a mobile resolution. Replaces the old hand-built mockup.
 *
 * The app doesn't refresh on its own when a brand's theme is republished, so the
 * iframe is remounted (full reload) whenever {@link reloadKey} changes — bump it
 * after a publish. A manual "Reload" affordance is also exposed.
 *
 * @param framed  Wrap the screen in a phone bezel (default). Pass `false` for an
 *                already-chromed context (e.g. the demo "embed in a site" card).
 * @param scale   Shrink factor from the logical {@link DEVICE} size. Tuned so the
 *                framed default (~0.72) sits in the portal's 360px side column.
 */
export function BrandPreviewFrame({
  brandId,
  reloadKey = 0,
  framed = true,
  scale = 0.72,
  preview,
  onPinVerdicts,
  onPinAwaiting,
}: {
  brandId: string;
  reloadKey?: number;
  framed?: boolean;
  scale?: number;
  /** Unsaved draft to push live into the embedded app. Omit for a published-only
   *  preview (e.g. the demo page). */
  preview?: ThemePreview;
  /** Each answer the app gives on the draft's pinned colours, with the colours
   *  it judged (so a consumer only applies it to that exact draft). */
  onPinVerdicts?: (report: PinVerdictReport | null) => void;
  /** Whether the app's answer on the latest draft is still owed (a v2 post is
   *  debouncing or unacked), so a publish can wait for it. */
  onPinAwaiting?: (awaiting: boolean) => void;
}) {
  const [loaded, setLoaded] = React.useState(false);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  // The embedded app announces when it can receive theme messages; until then a
  // post would be dropped (listener not attached). Reset on each remount.
  const [ready, setReady] = React.useState(false);
  // Whether THIS embed has acked a v2 message. Until it does we double-send
  // (v2 + legacy) so a v2-unaware build still previews; once acked, v2 only.
  const [v2Acked, setV2Acked] = React.useState(false);
  // Pairs the app's acks with the drafts they judged (no render depends on it).
  const pinsRef = React.useRef(NO_PIN_VERDICTS);
  const onPinVerdictsRef = React.useRef(onPinVerdicts);
  onPinVerdictsRef.current = onPinVerdicts;
  const onPinAwaitingRef = React.useRef(onPinAwaiting);
  onPinAwaitingRef.current = onPinAwaiting;
  const dispatchPins = React.useCallback((event: PinVerdictEvent) => {
    const prev = pinsRef.current;
    const next = reducePinVerdicts(prev, event);
    pinsRef.current = next;
    // Report first, then awaiting: a publish waiting on the ack reads the report.
    if (next.report !== prev.report) onPinVerdictsRef.current?.(next.report);
    if (isAwaiting(next) !== isAwaiting(prev)) onPinAwaitingRef.current?.(isAwaiting(next));
  }, []);
  // An unmounted frame owes no answer (a publish must not wait on it).
  React.useEffect(() => () => dispatchPins({ type: "reset" }), [dispatchPins]);
  // Last channel we emitted a "Preview Driven" event for (dedupe the 60ms pushes).
  const lastChannelRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    setReady(false);
    setLoaded(false);
    setV2Acked(false);
    dispatchPins({ type: "reset" });
    lastChannelRef.current = null;
  }, [brandId, reloadKey, dispatchPins]);

  // Listen for the embed's readiness ping AND its v2 ack (same guarded listener).
  React.useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== PLAY_ORIGIN) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const type = (e.data as { type?: string } | undefined)?.type;
      if (type === PREVIEW_READY_TYPE) setReady(true);
      else if (type === PREVIEW_V2_ACK_TYPE) {
        setV2Acked(true);
        dispatchPins({ type: "ack", data: e.data });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dispatchPins]);

  // Push the live draft into the app whenever it changes (debounced) and once
  // the app signals ready. Stringify for a stable effect dep + cheap equality.
  const previewKey = preview ? JSON.stringify(preview) : null;
  React.useEffect(() => {
    if (!ready || !previewKey) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    const p = JSON.parse(previewKey) as ThemePreview;
    // Owed from the moment the draft changes, not only once posted (the debounce).
    dispatchPins({ type: "scheduled", v2: !!p.recipe });
    const id = setTimeout(() => {
      const { channel, messages } = buildPreviewMessages(p, v2Acked);
      dispatchPins({ type: "sent", preview: p, v2: channel !== "legacy" });
      for (const m of messages) win.postMessage(m, PLAY_ORIGIN);
      // "Preview Driven" — one event per channel change, not per keystroke.
      if (lastChannelRef.current !== channel) {
        lastChannelRef.current = channel;
        track("Preview Driven", { channel, brandId });
      }
    }, 60);
    return () => clearTimeout(id);
  }, [ready, previewKey, v2Acked, brandId, dispatchPins]);

  // Reset the loading overlay whenever we force a reload (remount via `key`).
  const innerW = Math.round(DEVICE.width * scale);
  const innerH = Math.round(DEVICE.height * scale);

  const screen = (
    <div
      style={{
        position: "relative",
        width: innerW,
        height: innerH,
        overflow: "hidden",
        background: "#0a0a0f",
        borderRadius: framed ? 32 : 20,
      }}
    >
      <iframe
        ref={iframeRef}
        key={`${brandId}:${reloadKey}`}
        title="Live brand preview"
        src={previewUrl(brandId, reloadKey)}
        onLoad={() => setLoaded(true)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: DEVICE.width,
          height: DEVICE.height,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
        allow="clipboard-write; fullscreen"
      />
      {!loaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            background: "#0a0a0f",
            color: "rgba(255,255,255,.5)",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Loading preview…
        </div>
      )}
    </div>
  );

  if (!framed) return screen;

  // Phone bezel, matching the footprint the old mockup used.
  return (
    <div
      style={{
        borderRadius: 42,
        padding: 11,
        background: "#0a0a0f",
        boxShadow: "0 30px 70px rgba(0,0,0,.5), inset 0 0 0 1px rgba(255,255,255,.08)",
        flexShrink: 0,
      }}
    >
      {screen}
    </div>
  );
}
